import {
  faAngleDown,
  faAngleUp,
  faCheck,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { type ReactNode, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { BottomPortal } from '@sorare/core/src/atoms/layout/BottomPortal';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useSo5Competitions } from '@sorare/core/src/hooks/useSo5Competitions';
import { useUnclaimedMarketTasksCount } from '@sorare/core/src/hooks/users/useUnclaimedMarketTasksCount';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { SelectRarity } from 'atoms/SelectRarity';
import { TextSwitchRarity } from 'atoms/TextSwitchRarity';

import { SeriesBalance } from '../../commonCard/BuyPacks/SeriesBalance';
import { MobileMarketRoutesBlock } from '../MarketRoutesBlock';

const AUTHORIZED_RARITIES = [
  Rarity.common,
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
];

const Sticky = styled(Horizontal)`
  position: sticky;
  top: var(--current-stack-height);
  z-index: 10;
  height: 0;

  align-items: flex-start;
  @media ${tabletAndAbove} {
    justify-content: flex-end;
  }
`;

const FullSBHorizontal = styled(SBHorizontal)`
  width: 100%;
`;

const Container = styled.div`
  position: relative;
  --filter-height: 22px;
  --filter-min-width: var(--double-and-a-half-unit);
  display: flex;
  justify-content: center;
  width: 100%;
  min-width: 100%;
  gap: var(--unit);

  @media ${tabletAndAbove} {
    display: flex;
    width: unset;
    min-width: unset;
    justify-content: flex-start;
    gap: var(--unit);
    padding: var(--double-unit) 0;
  }
`;

const CompetitionFilter = styled(Horizontal)`
  justify-self: flex-end;
  @media ${tabletAndAbove} {
    justify-self: flex-start;
    padding: 0;
    position: static;
    right: unset;
    bottom: unset;
  }
`;

const RarityFilter = styled(Horizontal)`
  z-index: 1;
  & ul {
    --bar-bg-color: var(--c-black);
  }
`;

const CompetitionLogo = styled.img`
  height: var(--filter-height);
  width: 100%;
  object-fit: contain;
  .selected & {
    filter: grayscale(1) invert(1);
  }
`;

const StyledStarBall = styled(StarBall)`
  height: var(--filter-height);
  width: var(--filter-height);
  .selected & {
    filter: grayscale(1) invert(1);
  }
`;

const Option = styled(SBHorizontal)`
  cursor: pointer;
  gap: var(--unit);
  min-width: 280px;
  width: 100%;
  max-width: 100%;
  padding: var(--unit) var(--intermediate-unit);
  transition: 0.2s ease-in-out;
  &:hover {
    background-color: var(--c-nd-50);
  }
  &.is-selected {
    background-color: var(--c-nd-50);
  }
  @media ${tabletAndAbove} {
    min-width: 320px;
  }
`;

const Icon = styled.div`
  flex-shrink: 0;
  > * {
    width: var(--double-and-a-half-unit);
  }
`;

const CarbonIconWrapper = styled.span`
  filter: grayscale(1) invert(1);
  width: 100%;
  height: 100%;
  padding: var(--unit);
`;

type SwitchCompetitionProps = {
  competitionSlug: string;
  onChangeCompetition: (c: string) => void;
  options: {
    label: ReactNode;
    selectedLabel: string;
    value: string;
  }[];
};
const SwitchCompetition = ({
  competitionSlug,
  onChangeCompetition,
  options,
}: SwitchCompetitionProps) => {
  const isTablet = useIsTabletAndAbove();

  const selectedOption = options.find(
    option => option.value === competitionSlug
  );

  const ButtonComponent = isTablet ? Button : IconButton;

  return (
    <Dropdown
      gap={4}
      label={({ open, ...btnProps }) => (
        <ButtonComponent
          color={isTablet ? 'tertiary' : 'secondary'}
          size={isTablet ? 'small' : 'medium'}
          className={classNames({ active: open })}
          {...(isTablet && {
            endIcon: <FontAwesomeIcon icon={open ? faAngleUp : faAngleDown} />,
          })}
          {...btnProps}
        >
          {isTablet ? (
            <>
              <Icon>{selectedOption?.label}</Icon>
              <LabelL>{selectedOption?.selectedLabel}</LabelL>
            </>
          ) : (
            <CarbonIconWrapper>{selectedOption?.label}</CarbonIconWrapper>
          )}
        </ButtonComponent>
      )}
    >
      {({ closeDropdown }) => (
        <>
          {options.map(option => {
            return (
              <Option
                className={classNames({
                  'is-selected': option.value === competitionSlug,
                })}
                key={option.value}
                as={ButtonBase}
                onClick={() => {
                  closeDropdown();
                  onChangeCompetition(option.value);
                }}
              >
                <Horizontal as="span" gap={1.5}>
                  <Icon>{option.label}</Icon>
                  <LabelL color="var(--c-white)">{option.selectedLabel}</LabelL>
                </Horizontal>
                {option.value === competitionSlug && (
                  <FontAwesomeIcon icon={faCheck} />
                )}
              </Option>
            );
          })}
        </>
      )}
    </Dropdown>
  );
};

export const Filters = ({ sport }: { sport: Sport }) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const isSimplifiedNav = useSeriesNav();
  const { scoutableSo5Competitions } = useSo5Competitions();
  const [searchParams, setSearchParams] = useSearchParams();
  const buyGemsParam = searchParams.get('buy_gems');
  const rarityParam = searchParams.get('rarity');

  const unclaimedMarketTasksCount = useUnclaimedMarketTasksCount();

  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const rarity = isSimplifiedNav
    ? Rarity.common
    : lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport]?.rarity ||
      Rarity.limited;
  const competitionSlug =
    lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport]?.competitionSlug || '';

  const updateRarity = useCallback(
    (r: Rarity) => {
      updateLifecycle(LIFECYCLE.marketHomeFilters, {
        [sport]: {
          ...lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport],
          rarity: r,
        },
      });
    },
    [updateLifecycle, sport, lifecycle]
  );

  useEffect(() => {
    if (rarityParam === Rarity.common || buyGemsParam === 'true') {
      if (rarity !== Rarity.common) {
        updateRarity(Rarity.common);
      }
    }
    if (rarityParam) {
      setSearchParams(
        prev => {
          prev.delete('rarity');
          return prev;
        },
        {
          replace: true,
        }
      );
    }
  }, [rarityParam, buyGemsParam, rarity, updateRarity, setSearchParams]);

  const options = [
    {
      label: <StyledStarBall />,
      selectedLabel: 'All Star',
      value: 'DEFAULT',
    },
    ...scoutableSo5Competitions.map(competition => ({
      label: (
        <CompetitionLogo
          src={competition.iconUrl}
          alt={competition.displayName}
        />
      ),
      selectedLabel: competition.displayName,
      value: competition.slug,
    })),
  ];

  const onChangeRarity = (r: Rarity) => {
    updateRarity(r);
  };
  const onChangeCompetition = (c: string) => {
    updateLifecycle(LIFECYCLE.marketHomeFilters, {
      [sport]: {
        ...lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport],
        competitionSlug: c,
      },
    });
  };

  const hideCompetitionFilter =
    scoutableSo5Competitions.length < 2 || rarity === Rarity.common;

  const rarities = AUTHORIZED_RARITIES.filter(r =>
    sport === Sport.BASEBALL ? r !== Rarity.common : true
  );
  const raritiesWithBadgeContent = rarities.map(r => ({
    value: r,
    badgeContent: r === Rarity.common ? unclaimedMarketTasksCount : undefined,
  }));

  return (
    <Sticky>
      <MobileMarketRoutesBlock rarity={rarity} sport={sport} />
      <ConditionalWrapper wrap={!isTabletAndAbove} Wrapper={BottomPortal}>
        <FullSBHorizontal>
          <Container>
            <BottomPortal.Above>
              {!hideCompetitionFilter && (
                <CompetitionFilter>
                  <SwitchCompetition
                    competitionSlug={competitionSlug}
                    onChangeCompetition={onChangeCompetition}
                    options={options}
                  />
                </CompetitionFilter>
              )}
            </BottomPortal.Above>
            {!isSimplifiedNav && (
              <RarityFilter>
                {isTabletAndAbove ? (
                  <SelectRarity
                    rarities={raritiesWithBadgeContent}
                    selectedRarity={rarity}
                    onChange={onChangeRarity}
                    buttonSize="small"
                  />
                ) : (
                  <TextSwitchRarity
                    rarities={raritiesWithBadgeContent}
                    selectedRarity={rarity}
                    onChange={onChangeRarity}
                    transparent={false}
                    variant="secondary"
                    buttonSize="small"
                    withoutLabel={!isTabletAndAbove}
                    forceDesktopLayout
                  />
                )}
              </RarityFilter>
            )}
          </Container>
          {Rarity.common === rarity && <SeriesBalance />}
        </FullSBHorizontal>
      </ConditionalWrapper>
    </Sticky>
  );
};
